import React, { useRef, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../services/chatService';
import { CitationPanel } from './CitationPanel';
import { FeedbackButtons } from './FeedbackButtons';
import { FeedbackPanel } from './FeedbackPanel';

interface MessageListProps {
  messages: ChatMessage[];
  onFeedback: (messageIndex: number, rating: 'thumbs_up' | 'thumbs_down', feedbackText?: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, onFeedback }) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {messages.map((msg, idx) => (
          <Message key={idx} message={msg} messageIndex={idx} onFeedback={onFeedback} />
        ))}
        <div ref={endRef} />
      </div>
    </div>
  );
};

const Message: React.FC<{ message: ChatMessage; messageIndex: number; onFeedback: (messageIndex: number, rating: 'thumbs_up' | 'thumbs_down', feedbackText?: string) => void }> = ({ message, messageIndex, onFeedback }) => {
  const [showCitations, setShowCitations] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);
  const [currentRating, setCurrentRating] = useState<'thumbs_up' | 'thumbs_down' | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFeedbackClick = (rating: 'thumbs_up' | 'thumbs_down') => {
    if (currentRating === rating) {
      // Unclick - clear selection
      setCurrentRating(null);
      setShowFeedbackPanel(false);
    } else {
      // New selection
      setCurrentRating(rating);
      setShowFeedbackPanel(true);
    }
  };

  const handleFeedbackSubmit = (feedbackText: string) => {
    if (currentRating) {
      onFeedback(messageIndex, currentRating, feedbackText);
      setShowFeedbackPanel(false);
    }
  };

  const handleFeedbackCancel = () => {
    // Submit with current rating when closing
    if (currentRating) {
      onFeedback(messageIndex, currentRating, '');
    }
    setShowFeedbackPanel(false);
  };

  const handleCitationClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('citation-link')) {
      const match = target.textContent?.match(/^\[(\d+)\]$/);
      if (match) {
        const citationNum = match[1];
        if (!showCitations) setShowCitations(true);
        setTimeout(() => {
          document.getElementById(`citation-${citationNum}`)?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 100);
      }
    }
  };

  const processedContent = message.content.replace(
    /(\[\d+\])/g, 
    '<span class="citation-link">$1</span>'
  );

  return (
    <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-3xl ${message.role === 'user' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white'} rounded-lg p-4 shadow`}>
        {message.role === 'assistant' ? (
          <div className="prose dark:prose-invert max-w-none prose-sm" onClick={handleCitationClick}>
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {processedContent}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="whitespace-pre-wrap">{message.content}</div>
        )}
        
        {message.role === 'assistant' && !message.isWelcome && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {message.citations && message.citations.length > 0 && (
                  <button
                    onClick={() => setShowCitations(!showCitations)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {showCitations ? 'Hide' : 'Show'} Citations ({message.citations.length})
                  </button>
                )}
                <button
                  onClick={handleCopy}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <FeedbackButtons
                onFeedback={handleFeedbackClick}
                selectedRating={currentRating}
              />
            </div>
            {showFeedbackPanel && (
              <FeedbackPanel
                onSubmit={handleFeedbackSubmit}
                onCancel={handleFeedbackCancel}
              />
            )}
            {showCitations && message.citations && <CitationPanel citations={message.citations} />}
          </div>
        )}
      </div>
    </div>
  );
};
